// « Connecteurs du bureau » (route #/connectors[/<connectorId>]; « Connecteurs » in the user menu
// for everyone while the server reports features.connectorLibrary): the office's connector
// library — every connector of this office, which ones the agents may use for this person, and
// the ones he may add.
// - « Comment ça marche »: one library per office, on every floor (an agent gets a connector in
//   its settings), the administrator decides who may use and who may add (one's own additions are
//   allowed for oneself), connecting a service lends one's account to the agents.
// - Someone not allowed to add: the server's note (`message`), no « Ajouter ».
// - « Ajoutés dans le bureau »: the library's connectors (GET /api/connectors/library), each with
//   its name, host, state (À CONNECTER · CONNECTÉ · À RECONNECTER · ERREUR), tool counts, who added
//   it and when, « autorisé pour vous » or not (a member: his « Connecteurs autorisés »); and, on
//   the ones he may manage (an administrator any; a member allowed to add, his own), CONNECTER /
//   RECONNECTER / REPRENDRE, Vérifier, Renommer and Retirer (myconnectors/actions.js). A filter
//   above longer lists. AJOUTER UN CONNECTEUR (for whoever may add) brings the add panel into view.
// - « Connectés sur claude.ai ou le serveur »: the office's detected connectors (the store's
//   `connectors`), read-only here, with « autorisé pour vous » or not.
// - « Ajouter un connecteur » (myconnectors/add.js): the catalogue cards (Canva, Shopify, Mailgun:
//   each as many times as there are accounts, under its own name) and « Autre service » (open from
//   the start); a new connector still to connect opens its connect window at once. A plugin the
//   office serves itself shows its details to its managers (store, region, the last characters of
//   its key) and opens its settings window instead of a login (myconnectors/plugin.js).
// - Back from Shopify (#/connectors/<id>?shopify=ok|error|…, « Se connecter avec Shopify »): a
//   toast says how it went (the connector's own error when it failed), then the address is cleaned.
//   An arrival from Shopify with no connector chosen yet (#/connectors?shopify=launch&l=<id>: the
//   store picked in Shopify's « Install app » or the app opened from the Shopify admin) opens the
//   confirm window (myconnectors/plugin.js openShopifyLaunch); a refused or expired one
//   (shopify=launch-invalid | launch-expired) is a toast.
// - A Google Drive (myconnectors/drive.js): added like a Shopify store (its card here, then its
//   connect window at once); its status line says the folder it is limited to (a lock) or the next
//   step (SE CONNECTER → CHOISIR LE DOSSIER); « Fichiers du bureau » on the one storing the office's
//   files (everyone sees it); back from Google (#/connectors/<id>?drive=ok|error|…, « Se
//   connecter avec Google »): a toast says how it went (the connector's own error when it failed),
//   then the address is cleaned; connected without a folder yet, its settings window opens (the
//   folder is the next step).
// The library is the store's `libraryConnectors` slice (the shell loads it for the user-menu hint
// and applies the live events); the page reads GET /api/connectors/library on open (permission,
// catalogue, limits), again when the person's permission or policy changes (`me`), and applies the
// events too. A deep link #/connectors/<id> (a notification's « Ouvrir ») opens that connector's
// window once loaded only when it was never connected (À CONNECTER) and the person may manage it,
// else brings its row into view: a login is started only by a click, since starting one drops the
// stored login. Text written by people is shown as text.
import { h, button, input, badge, statusDot, emptyState, spinner, icon, uid } from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatDateTime } from '../format.js';
import { iconForConnector } from './connectoricons.js';
import { errorText } from './admin/model.js';
import { alertBox } from './admin/common.js';
import { createLibraryActions } from './myconnectors/actions.js';
import { createAddPanel } from './myconnectors/add.js';
import { openShopifyLaunch } from './myconnectors/plugin.js';
import { driveReturnOf, driveReturnToast, GDRIVE_ID } from './myconnectors/drive-model.js';
import {
  normalizeLibrary, liveConnectorOf, withConnector, withoutConnector, stateView, libraryAccess, detectedConnectors,
  addedByText, toolsText, filterLibrary, isPluginConnector, isShopifyCli, settingsActionKey, pluginDetails, shopifyReturnOf, shopifyRoute, LIBRARY_BASE,
} from './myconnectors/model.js';
import './myconnectors/strings.js';

export { WRITE_TIMEOUT_MS, CHECK_TIMEOUT_MS } from './myconnectors/actions.js';

/** Relative dates (« il y a 5 min ») are refreshed every minute while the page is open. */
const MINUTE_MS = 60_000;
/** A filter box is shown above longer lists. */
const FILTER_MIN = 10;
const DET_TONES = { connected: 'ok', 'needs-auth': 'warn', failed: 'error' };

/** What makes the page read its answer again: the permission, the role, the policy. */
function meKey(me) {
  return JSON.stringify([me?.id ?? null, me?.role ?? null, me?.disabled === true, libraryAccess({ features: { connectorLibrary: true }, me }).canAdd,
    me?.connectorPolicy ?? null]);
}

/**
 * Opens the page (a drawer).
 * @param {object} ctx app context
 * @param {string} [connectorId] a connector to open (deep link from a notification)
 * @param {{shopify?: string|null, launchId?: string|null, drive?: string|null, navigate?: (url: string) => void}} [opts]
 *   shopify: the outcome of a return from (or an arrival from) Shopify (shopifyReturnOf(location.hash)),
 *   said once the list is loaded; launchId: the arrival waiting for a choice (outcome `launch`);
 *   drive: the outcome of a return from Google (driveReturnOf(location.hash)), said once the list
 *   is loaded; navigate: the browser goes to Shopify (tests pass their own)
 * @returns {import('./overlay.js').Layer}
 */
export function openMyConnectors(ctx, connectorId, {
  shopify = shopifyReturnOf(globalThis.location?.hash)?.outcome ?? null,
  launchId = shopifyReturnOf(globalThis.location?.hash)?.launchId ?? null,
  drive = driveReturnOf(globalThis.location?.hash)?.outcome ?? null,
  navigate,
} = {}) {
  const list = () => ctx.store.get().libraryConnectors || [];
  const st = {
    status: ctx.store.get().libraryConnectorsLoaded ? 'ok' : 'loading', // loading · ok · unavailable · error
    error: '',
    data: null, // normalizeLibrary() of the last GET (permission, catalogue, limits)
    query: '',
  };
  let target = connectorId || null;
  let shopifyOutcome = shopify;
  let driveOutcome = drive;
  let launchLayer = null; // the confirm window of an arrival from Shopify
  let highlight = null;
  let layer = null;
  let seq = 0;
  const offs = [];
  const isOpen = () => !!layer?.isOpen();
  const serverNow = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  // May add: the server's answer once read, else what `me` says.
  const canAdd = () => (st.data ? st.data.canAdd : libraryAccess(ctx.store.get()).canAdd);
  const isMember = () => ctx.store.get().me?.role !== 'admin';
  const fk = (el, key) => { el.dataset.fk = key; return el; };
  const setList = (next) => {
    if (next !== list() || !ctx.store.get().libraryConnectorsLoaded) ctx.store.set({ libraryConnectors: next, libraryConnectorsLoaded: true });
  };

  const actions = createLibraryActions(ctx, {
    isOpen,
    list,
    render: () => render(),
    onSaved: (c) => setList(withConnector(list(), c)),
    onRemoved: (c) => setList(withoutConnector(list(), c.id)),
    onLoginDone: () => load({ quiet: true }),
  });

  // --- static parts --------------------------------------------------------------------------------
  const explainId = uid('pc-explain');
  const item = (name, key) => h('li', { class: 'pc-explain-item' }, icon(name, { size: 16 }), h('span', {}, t(key)));
  const explainEl = h('section', { class: 'pc-explain', 'aria-labelledby': explainId },
    h('h3', { class: 'pc-explain-title mono-label', id: explainId }, t('pc.explain.title')),
    h('ul', { class: 'pc-explain-list' },
      item('layers', 'pc.explain.office'), item('building', 'pc.explain.floors'), item('shield', 'pc.explain.policy'), item('key', 'pc.explain.account')));
  const noticeEl = h('div', { class: 'pc-notice' });

  const libTitleId = uid('pc-lib');
  const libCount = h('span', { class: 'pc-count mono-label' });
  const addBtn = fk(button(t('pc.addButton'), { variant: 'primary', size: 'sm', icon: 'plus', class: 'pc-addbtn', onClick: () => addPanel.focus() }), 'pc-addbtn');
  const filterInput = fk(input({
    type: 'search', class: 'input-sm pc-filter', placeholder: t('pc.filter'), 'aria-label': t('pc.filter'), hidden: true,
    onInput: (e) => { st.query = e.target.value; renderLibrary(); },
  }), 'pc-filter');
  const libHost = h('div', { class: 'pc-listhost' });
  const libSection = h('section', { class: 'pc-section pc-library', 'aria-labelledby': libTitleId },
    h('div', { class: 'pc-section-head' },
      h('h3', { class: 'pc-section-title', id: libTitleId }, h('span', {}, t('pc.library.title')), libCount),
      addBtn),
    filterInput, libHost);

  const detTitleId = uid('pc-det');
  const detCount = h('span', { class: 'pc-count mono-label' });
  const detHost = h('div', { class: 'pc-listhost' });
  const detSection = h('section', { class: 'pc-section pc-detected', 'aria-labelledby': detTitleId },
    h('h3', { class: 'pc-section-title', id: detTitleId }, h('span', {}, t('pc.detected.title')), detCount),
    h('p', { class: 'pc-section-hint' }, t('pc.detected.hint')),
    detHost);

  const addPanel = createAddPanel(ctx, {
    isOpen,
    onAdded: (saved) => {
      setList(withConnector(list(), saved));
      // A remote one logs in now, a Shopify store through the Shopify CLI and a Google Drive too
      // (its connect window: the Google account, then the folder); another plugin is connected
      // already (or on its way to Shopify).
      if (!isPluginConnector(saved) || isShopifyCli(saved) || saved.catalogId === GDRIVE_ID) actions.connect(saved);
    },
  });

  const stateHost = h('div', { class: 'pc-statehost' });
  const content = h('div', { class: 'pc-content' }, explainEl, noticeEl, libSection, detSection, addPanel.el);
  const root = h('div', { class: 'pc-root' }, stateHost, content);

  // --- rendering -----------------------------------------------------------------------------------
  function actionButton(c, action, opts) {
    return fk(button(t(`pc.action.${action}`), {
      size: 'sm', disabled: actions.busy.has(c.id), ariaLabel: t('pc.actionAria', { action: t(`pc.action.${action}`), name: c.label }), ...opts,
    }), `${action}:${c.id}`);
  }

  /** « FICHIERS DU BUREAU » on the Google Drive storing the office's files (everyone sees it). */
  function storageTag(c) {
    if (c.catalogId !== GDRIVE_ID || c.storage !== true) return null;
    return badge(t('pc.drive.storageBadge'), 'info', { class: 'pc-conn-storage', title: t('pc.drive.storageBadgeTitle') });
  }

  /** « AUTORISÉ POUR VOUS » / « PAS POUR VOUS » (a member only: an administrator is never restricted). */
  function forYouTag(allowedForMe) {
    if (!isMember()) return null;
    return h('span', {
      class: ['pc-foryou mono-label', allowedForMe ? 'is-on' : 'is-off'],
      title: t(allowedForMe ? 'pc.forYou.yesHint' : 'pc.forYou.noHint'),
    }, t(allowedForMe ? 'pc.forYou.yes' : 'pc.forYou.no'));
  }

  function row(c) {
    const running = actions.running.has(c.id);
    // A Shopify store to connect says what « Se connecter » does here (once the library is read).
    const v = stateView(c, { jobActive: running, shopifyRoute: st.data ? shopifyRoute(st.data) : undefined });
    const now = serverNow();
    const meta = [
      c.addedAt ? `${addedByText(c)} ${relativeTime(c.addedAt, now)}` : addedByText(c),
      t('pc.everyFloor'),
      c.status === 'connected' && c.connectedAt ? t('pc.connectedAt', { when: relativeTime(c.connectedAt, now) }) : '',
      c.checkedAt ? t('pc.checkedAt', { when: relativeTime(c.checkedAt, now) }) : '',
    ].filter(Boolean).join(' · ');
    const buttons = [];
    if (v.primary === 'settings') {
      buttons.push(actionButton(c, v.settingsLabel, { variant: 'primary', icon: v.primaryIcon || 'key', onClick: () => actions.settings(c) }));
    } else if (v.primary) {
      const resume = v.primary === 'resume';
      buttons.push(actionButton(c, v.primary, { variant: 'primary', icon: 'key', onClick: () => actions.connect(c, { resume }) }));
    }
    if (v.settings) {
      const label = t(`pc.action.settings.${settingsActionKey(c)}`);
      buttons.push(fk(button(label, {
        size: 'sm', variant: 'ghost', icon: 'key', disabled: actions.busy.has(c.id), ariaLabel: t('pc.actionAria', { action: label, name: c.label }),
        onClick: () => actions.settings(c),
      }), `settings:${c.id}`));
    }
    if (v.reconnect) buttons.push(actionButton(c, 'reconnect', { variant: 'ghost', icon: 'key', onClick: () => actions.reconnect(c) }));
    if (v.check) buttons.push(actionButton(c, 'check', { variant: 'ghost', icon: 'refresh', onClick: () => actions.check(c) }));
    if (v.manage) {
      buttons.push(actionButton(c, 'rename', { variant: 'ghost', icon: 'pencil', onClick: () => actions.rename(c) }));
      buttons.push(actionButton(c, 'remove', { variant: 'ghost', icon: 'close', class: 'pc-del', onClick: () => actions.remove(c) }));
    }
    return h('li', {
      class: ['pc-conn', `is-${v.status}`, { 'is-needs-folder': v.needsFolder, 'is-busy': actions.busy.has(c.id), 'is-target': highlight === c.id, 'is-mine': c.mine }],
      dataset: { id: c.id },
    },
    h('div', { class: 'pc-conn-head' },
      iconForConnector({ key: c.catalogId || c.id, name: c.label }, { size: 32 }),
      h('div', { class: 'pc-conn-titles' },
        h('h4', { class: 'pc-conn-name' }, c.label),
        c.host ? h('p', { class: 'pc-conn-host mono-label' }, c.host) : null),
      storageTag(c),
      forYouTag(c.allowedForMe),
      badge(v.badge, v.tone)),
    h('p', { class: ['pc-conn-status', { 'has-lead': v.lead }] }, v.lead ? icon(v.lead, { size: 15, class: 'pc-conn-lead' }) : statusDot(v.dot), h('span', {}, v.text)),
    // A Google Drive's folder and account are its status line already.
    v.plugin && v.manage && c.catalogId !== GDRIVE_ID && pluginDetails(c) ? h('p', { class: 'pc-conn-plugin mono-label' }, pluginDetails(c)) : null,
    running && v.manage ? h('p', { class: 'pc-conn-running' }, t('pc.jobRunning')) : null,
    h('p', { class: 'pc-conn-meta', title: c.addedAt ? formatDateTime(c.addedAt, now) : null }, meta),
    buttons.length ? h('div', { class: 'pc-conn-actions' }, buttons) : null);
  }

  function renderLibrary() {
    const all = list();
    libCount.textContent = all.length ? t('pc.count', { n: all.length }) : '';
    addBtn.hidden = !canAdd();
    filterInput.hidden = all.length <= FILTER_MIN;
    if (!all.length) {
      libHost.replaceChildren(emptyState({ icon: 'layers', title: t('pc.empty.title'), text: canAdd() ? t('pc.empty.text') : undefined }));
      return;
    }
    const shown = filterLibrary(all, filterInput.hidden ? '' : st.query);
    libHost.replaceChildren(shown.length
      ? h('ul', { class: 'pc-list', 'aria-label': t('pc.library.label') }, shown.map(row))
      : h('p', { class: 'muted pc-nomatch' }, t('pc.noMatch')));
  }

  function detectedRow(d) {
    const tone = DET_TONES[d.status] || 'off';
    const tools = toolsText({ toolCount: d.toolCount, readCount: d.readCount, writeCount: d.writeCount });
    return h('li', { class: ['pc-det', `is-${tone}`], dataset: { key: d.key } },
      iconForConnector(d.server, { size: 28 }),
      h('div', { class: 'pc-det-main' },
        h('p', { class: 'pc-det-name' }, d.name),
        h('p', { class: 'pc-det-meta mono-label' }, [t(`pc.engine.${d.engine}`), tools].filter(Boolean).join(' · '))),
      forYouTag(d.allowedForMe),
      h('span', { class: 'pc-det-status' }, statusDot(tone), h('span', {}, t(`connector.status.${['connected', 'needs-auth', 'failed'].includes(d.status) ? d.status : 'unknown'}`))));
  }

  function renderDetected() {
    const rows = detectedConnectors(ctx.store.get());
    detCount.textContent = rows.length ? t('pc.count', { n: rows.length }) : '';
    detHost.replaceChildren(rows.length
      ? h('ul', { class: 'pc-detlist', 'aria-label': t('pc.detected.label') }, rows.map(detectedRow))
      : h('p', { class: 'muted pc-det-empty' }, t('pc.detected.empty')));
  }

  function renderNotice() {
    if (canAdd()) { noticeEl.replaceChildren(); noticeEl.hidden = true; return; }
    noticeEl.hidden = false;
    noticeEl.replaceChildren(alertBox('info', st.data?.message || t('pc.notAllowed')));
  }

  function renderAdd() {
    addPanel.el.hidden = !canAdd();
    if (addPanel.el.hidden) return;
    addPanel.update({ data: st.data, list: list() });
  }

  function stateContent() {
    if (st.status === 'loading') return h('div', { class: 'pc-state' }, spinner(t('pc.loading')), h('span', { class: 'muted' }, t('pc.loading')));
    // The server says why when it has the feature off (e.g. the Claude settings it needs).
    if (st.status === 'unavailable') return emptyState({ icon: 'info', title: t('pc.unavailable'), text: st.data?.message || t('pc.unavailableHint') });
    return emptyState({
      icon: 'alert', title: t('common.error'), text: st.error,
      action: fk(button(t('pc.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => load() }), 'retry'),
    });
  }

  let rendering = false;
  function render() {
    if (!isOpen() || rendering) return;
    rendering = true;
    try {
      const active = globalThis.document?.activeElement;
      const focusKey = active?.dataset?.fk || null;
      const ok = st.status === 'ok';
      stateHost.hidden = ok;
      content.hidden = !ok;
      if (!ok) {
        stateHost.replaceChildren(stateContent());
      } else {
        renderNotice();
        renderLibrary();
        renderDetected();
        renderAdd();
      }
      if (focusKey) {
        const again = [...root.querySelectorAll('[data-fk]')].find((el) => el.dataset.fk === focusKey);
        // Without preventScroll, every re-render (event, minute tick) would scroll back to it.
        if (again && again !== active) again.focus?.({ preventScroll: true });
      }
      if (highlight && ok) {
        const el = [...libHost.querySelectorAll('[data-id]')].find((x) => x.dataset.id === highlight);
        el?.scrollIntoView?.({ block: 'nearest' });
        highlight = null;
      }
    } finally {
      rendering = false;
    }
  }

  /**
   * The deep link (#/connectors/<id>), once the list is known: the connect window of a connector
   * never connected that the person may manage; any other is only shown (its RECONNECTER is on
   * the row), so following a notification never drops a login that may still work.
   */
  function openTarget() {
    if (!target || st.status !== 'ok') return;
    const id = target;
    target = null;
    const c = list().find((x) => x.id === id);
    if (shopifyOutcome) {
      sayShopify(c);
      if (c) highlight = id;
      return;
    }
    if (driveOutcome) {
      sayDrive(c);
      if (c) highlight = id;
      return;
    }
    if (!c) { ctx.toast.warn(t('pc.gone')); return; }
    if (c.status === 'added' && c.canManage) actions.connect(c);
    else highlight = id;
  }

  /**
   * Back from Shopify: how it went, once; an arrival with no connector chosen: its confirm window.
   * The address loses its ?shopify=… (a reload says nothing).
   */
  function sayShopify(c) {
    const outcome = shopifyOutcome;
    shopifyOutcome = null;
    if (outcome === 'launch' && launchId) confirmLaunch(launchId);
    else if (outcome === 'ok' && c) ctx.toast.success(t('pc.shopify.return.ok', { name: c.label }));
    else if (outcome === 'error' || outcome === 'ok') ctx.toast.error(c?.lastError || t('pc.shopify.return.error'));
    else if (outcome === 'launch-invalid' || outcome === 'launch') ctx.toast.error(t('pc.shopify.return.launch-invalid'));
    else ctx.toast.warn(t(`pc.shopify.return.${outcome}`));
    try {
      const hash = String(globalThis.location?.hash || '');
      if (hash.includes('shopify=')) globalThis.history?.replaceState?.(null, '', hash.split('?')[0]);
    } catch { /* the address stays as it is */ }
  }

  /**
   * Back from Google: how it went, once (the connector's own error when it failed); connected
   * without a folder yet, its settings window (the folder is the next step). The address loses its
   * ?drive=… (a reload says nothing).
   */
  function sayDrive(c) {
    const outcome = driveOutcome;
    driveOutcome = null;
    const msg = driveReturnToast(outcome, c || null);
    ctx.toast[msg.kind](msg.text);
    if (outcome === 'ok' && c?.canManage && c.status === 'connected' && !c.plugin?.folder) actions.settings(c);
    try {
      const hash = String(globalThis.location?.hash || '');
      if (hash.includes('drive=')) globalThis.history?.replaceState?.(null, '', hash.split('?')[0]);
    } catch { /* the address stays as it is */ }
  }

  /** « Connecter la boutique … ? »: closed with the page; a new connector reads the list again. */
  async function confirmLaunch(id) {
    const opened = await openShopifyLaunch(ctx, id, { navigate, onAdded: () => load({ quiet: true }) });
    if (opened && !isOpen()) opened.close('route');
    else launchLayer = opened;
  }

  // --- data ----------------------------------------------------------------------------------------
  async function load({ quiet = false } = {}) {
    const my = ++seq;
    if (!quiet && st.status !== 'ok') { st.status = 'loading'; render(); }
    try {
      const data = normalizeLibrary(await ctx.api.get(LIBRARY_BASE));
      if (my !== seq || !isOpen()) return;
      st.data = data;
      st.error = '';
      st.status = data.enabled ? 'ok' : 'unavailable';
      // The same list as the one held changes nothing (no re-render of the rows).
      setList(JSON.stringify(data.connectors) === JSON.stringify(list()) ? list() : data.connectors);
      openTarget();
      // A return from Shopify (Google) without a connector in the address (expired, another session…).
      if (shopifyOutcome && st.status === 'ok') sayShopify(null);
      if (driveOutcome && st.status === 'ok') sayDrive(null);
    } catch (err) {
      if (my !== seq || !isOpen()) return;
      if (err?.status === 404) st.status = 'unavailable';
      else {
        // A list already known stays (the shell keeps it current); the add part says what failed.
        st.error = errorText(err);
        if (st.status !== 'ok') st.status = 'error';
      }
    }
    render();
  }

  // --- open ----------------------------------------------------------------------------------------
  layer = ctx.overlay.openDrawer({
    title: t('pc.title'), eyebrow: t('pc.eyebrow'), subtitle: t('pc.subtitle'), size: 'lg',
    className: 'pc-drawer', content: root,
  });
  layer.onClose?.(() => actions.closeAll());

  offs.push(ctx.events.on('connector-library', (d) => {
    const c = liveConnectorOf(d, ctx.store.get(), list());
    if (!c) return;
    const wasRunning = actions.running.delete(c.id);
    setList(withConnector(list(), c));
    if (wasRunning) render();
  }));
  offs.push(ctx.events.on('connector-library.removed', (d) => {
    if (!d?.id) return;
    actions.running.delete(d.id);
    setList(withoutConnector(list(), d.id));
  }));
  offs.push(ctx.store.on('libraryConnectors', () => render()));
  offs.push(ctx.store.on('connectors', () => render()));
  // The administrator changed the permission or the policy (a new `me`): read the answer again.
  let lastMe = meKey(ctx.store.get().me);
  offs.push(ctx.store.on('me', () => {
    const now = meKey(ctx.store.get().me);
    if (now === lastMe) return;
    lastMe = now;
    load({ quiet: true });
  }));

  let minuteTimer = setInterval(() => render(), MINUTE_MS);
  layer.onClose(() => {
    for (const off of offs) { try { off?.(); } catch { /* ignore */ } }
    clearInterval(minuteTimer);
    minuteTimer = null;
    seq += 1;
    addPanel.reset();
    if (launchLayer?.isOpen?.()) launchLayer.close('route');
  });

  render();
  load();
  return layer;
}
